/**
 * Created at 2023/08/18
 */
import { findFieldsByType } from '../store';
import {
  GO,
  BPSymbol,
  TKJobItem,
  DETECTION_MODE,
  SYMBOL_REVISION,
} from '../types';
import {
  filterCompletionFields,
  getReducedFieldsByShape,
} from '../types/dimension';

/**
 * Compose job item state including `vers`, `completed`, `confirmed`
 * DO NOT LOOK `confirmed` as `completed`!
 * @date 2024/04/15
 *
 * @log vers equals to `D` is also considered completed - @2024/05/29
 *
 * @log check `completed` from detail if its not `confirmed` - @2024/12/09
 *
 * @log strict `completed` only determind by version - @2025/02/20
 *
 * @param item
 * @param detail
 * @returns
 */
export const checkJobItemState = (item: TKJobItem, detail: GO) => {
  const { detection_mode } = item;
  const confirmed = !!item.user_validated;
  const vers = checkJobItemVersionBy(
    item.item.id,
    detail,
    confirmed,
    detection_mode,
  );
  const isCompleted = vers === SYMBOL_REVISION.C;
  // const isConfirmed = vers === SYMBOL_REVISION.D;
  // const isManualSymbol = vers === SYMBOL_REVISION.M;
  // const completed = isCompleted || isConfirmed || isManualSymbol;
  // ! FIXME: only check full dimension values to treat it `completed`
  // @2025/02/20
  return { vers, completed: isCompleted, confirmed };
};

/**
 * General method to check symbol version:
 * Check symbol version if all dimension completed on first round features rendering
 *
 * NOTE: if detection_mode is MANUAL, the version is 4
 *
 * USE CASE:
 * 1. jobItemTransformer at the jot items loading and transformation
 *
 * Add `shape` checking for version inference - @2023/11/03
 *
 * FIXME: check operation mode to decide version
 * @date 2024/03/21
 *
 * @param symbolType
 * @param detail symbol detail
 * @param userValidated optional user confirmed
 * @param mode optional mode, will returen vers 4 if detection_mode is MANUAL
 * @returns version number
 */
const checkJobItemVersionBy = (
  itemType: string,
  detail: GO,
  userValidated?: boolean,
  mode?: string,
) => {
  // == case - 1: MANUAL SYMBOL
  if (mode === DETECTION_MODE.M) return SYMBOL_REVISION.M;
  // == case - 2: check manual confirmation first!
  if (userValidated) return SYMBOL_REVISION.D;

  // == case - 3: normal fieds empty checking
  const definedFields = findFieldsByType(itemType);
  const commonFields = ['system', ...definedFields];
  const completionFields = filterCompletionFields(commonFields);
  // consider `shape` for dimension
  const reducedFields = getReducedFieldsByShape(
    completionFields,
    detail['shape'],
  );

  const checkExistence = reducedFields.map(
    (f) => !!detail[f] && detail[f] !== '0',
  );
  const haveEmptyValue = checkExistence.includes(false);

  return haveEmptyValue ? SYMBOL_REVISION.B : SYMBOL_REVISION.C;
};

/**
 * Calculate completion percentage of a symbol in real time of symbol correction
 * FIXME: '0' input not a valid size to count for completion value
 * @2023/11/08
 *
 * @param symbol
 * @param fields dimension properties without 'system'
 * @returns
 */
export const completionCalculator = (
  symbol: BPSymbol,
  fields: string[],
  hasSystemDefined: boolean,
): number => {
  const dimensions = symbol.detail;
  if (!dimensions) return 0;
  // FIXME: check no fields but `system` field case:
  // such as `TOILET`
  // @2024/12/06
  if (!fields.length && dimensions['system']) return 100;
  // check `shape`
  const { shape } = symbol.detail;
  if (fields.length == 0) return 0;
  const completionFields = filterCompletionFields(fields);
  const reducedFields = getReducedFieldsByShape(completionFields, shape);
  const finalFieldsForCompletion = hasSystemDefined
    ? [...reducedFields, 'system']
    : reducedFields;
  // re-evaluate share size!
  const eachShare = Math.ceil(100 / finalFieldsForCompletion.length);
  const completion = finalFieldsForCompletion.reduce(
    (total: number, field: string) => {
      const validInput = !!dimensions[field] && dimensions[field] !== '0';
      return validInput ? total + eachShare : total;
    },
    0,
  );
  // console.log({ completion });
  // FIXME: sometimes, the whole cells filled, but the completion is 99
  // so, +1 is required to make it `completed`!
  // @2023/11/10
  if (completion >= 99) return 100;

  return Math.min(completion, 100);
};
